import { apiRequest } from '../lib/api-client';

/**
 * Creating the records the list screens show (context.md §16, §106–§115).
 *
 * Separate from `api.ts`, which is search, board and views: reading a list and adding to it are
 * different jobs, and the search module was already the longest thing in this folder.
 *
 * The shapes deliberately mirror the server's schemas rather than the row shapes that come back —
 * a `contact` is written as `firstName` and read as `first_name`, and pretending otherwise on one
 * side produces a field that silently never saves.
 */

export interface ContactInput {
  firstName: string;
  lastName?: string;
  companyId?: string | null;
  email?: string;
  phone?: string;
  mobile?: string;
  jobTitle?: string;
  ownerId?: string | null;
}

export function createContact(input: ContactInput): Promise<{ id: string }> {
  return apiRequest('/v1/crm/contacts', { method: 'POST', body: input });
}

export interface CompanyInput {
  name: string;
  statusId?: string | null;
  sourceId?: string | null;
  email?: string;
  phone?: string;
  website?: string;
  industry?: string;
  employeeCount?: number | null;
  ownerId?: string | null;
}

export function createCompany(input: CompanyInput): Promise<{ id: string }> {
  return apiRequest('/v1/crm/companies', { method: 'POST', body: input });
}

/**
 * `acme.com.br` as the URL the server will accept.
 *
 * The schema validates a URL and nobody types a scheme. Refusing the form over a missing `https://`
 * teaches people to leave the field empty, which loses the one field that makes a company findable
 * again — so the browser completes what it can be sure of, and leaves anything already absolute
 * alone rather than guessing at it.
 */
export function withScheme(value: string): string {
  const trimmed = value.trim();

  if (trimmed === '') return trimmed;

  // Any scheme, not only http: `mailto:` or a typo'd `htp://` should reach the server as typed and
  // be refused there, rather than being quietly turned into `https://mailto:...`.
  return /^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
}
